import React, { useContext, useState } from 'react'
import withAuth from '../utils/withAuth'
import { useNavigate } from 'react-router-dom'
import "../App.css";
import { Box, Button, Chip, Container, Divider, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { AuthContext } from '../contexts/AuthContext';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';

function HomeComponent() {
    let navigate = useNavigate();
    const [meetingCode, setMeetingCode] = useState("");
    const { addToUserHistory } = useContext(AuthContext);

    const generateRandomCode = () => {
        // Generates an 8-character string, e.g. "abc-defg"
        const chars = "abcdefghijklmnopqrstuvwxyz";
        let part1 = "";
        let part2 = "";
        for (let i = 0; i < 3; i++) {
            part1 += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        for (let i = 0; i < 4; i++) {
            part2 += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `${part1}-${part2}`;
    }

    let handleJoinVideoCall = async () => {
        if (!meetingCode.trim()) return;
        await addToUserHistory(meetingCode);
        navigate(`/${meetingCode}`);
    }

    let handleCreateMeeting = async () => {
        const randomCode = generateRandomCode();
        await addToUserHistory(randomCode);
        navigate(`/${randomCode}`);
    }

    return (
        <>
            <Box className="homeShell">
                <div className="navBar">
                    <div className="homeNavLeft">
                        <div className="brandMark" aria-hidden="true" />
                        <div>
                            <h2>AK Meet</h2>
                            <p className="homeNavSubtitle">Meetings that feel effortless.</p>
                        </div>
                    </div>

                    <div className="homeNavRight">
                        <Button
                            startIcon={<RestoreIcon />}
                            onClick={() => {
                                navigate("/history")
                            }}
                            variant="text"
                        >
                            History
                        </Button>

                        <Button
                            onClick={() => {
                                localStorage.removeItem("token")
                                navigate("/auth")
                            }}
                            variant="outlined"
                            color="secondary"
                            sx={{ border: '1px solid rgba(255, 255, 255, 0.2)' }}
                        >
                            Logout
                        </Button>
                    </div>
                </div>

                <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, flex: 1, display: 'flex', alignItems: 'center' }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Paper elevation={10} className="homeJoinCard">
                                <Stack spacing={3}>
                                    <Box>
                                        <Typography variant="h4" fontWeight={400} sx={{ color: '#ffffff', mb: 1 }}>
                                            Premium video meetings.
                                            <br />
                                            Now free for everyone.
                                        </Typography>
                                        <Typography color="text.secondary" sx={{ fontSize: '1rem', mb: 2 }}>
                                            We redesigned AK Meet to bring you crisp audio, secure video calls, and instant messaging—all built for learners and creators.
                                        </Typography>
                                    </Box>

                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                        <Button 
                                            onClick={handleCreateMeeting} 
                                            variant="contained" 
                                            color="primary" 
                                            size="large" 
                                            startIcon={<VideoCallIcon />}
                                            sx={{ 
                                                py: 1.8, 
                                                px: 3, 
                                                whiteSpace: 'nowrap', 
                                                borderRadius: 2,
                                                fontSize: '0.95rem',
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            New meeting
                                        </Button>

                                        <Stack direction="row" spacing={1} sx={{ width: '100%', flex: 1 }}>
                                            <TextField
                                                fullWidth
                                                onChange={e => setMeetingCode(e.target.value)}
                                                id="meeting-code"
                                                placeholder="Enter a code or link"
                                                variant="outlined"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                                    }
                                                }}
                                            />
                                            <Button 
                                                onClick={handleJoinVideoCall} 
                                                variant="text" 
                                                color="primary"
                                                disabled={!meetingCode.trim()}
                                                sx={{ 
                                                    px: 3, 
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    '&:disabled': {
                                                        color: 'rgba(255, 255, 255, 0.3)'
                                                    }
                                                }}
                                            >
                                                Join
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Chip icon={<BoltIcon />} label="Instant join" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                        <Chip icon={<ShieldIcon />} label="Secure connection" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                        <Chip icon={<RestoreIcon />} label="Activity logs" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                                    </Stack>

                                    <Box className="homeTipStrip">
                                        <Typography fontWeight={500} color="primary" sx={{ mb: 0.5 }}>Pro tip</Typography>
                                        <Typography color="text.secondary" variant="body2">
                                            Make sure to allow camera and microphone permissions when prompted to join calls smoothly.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Paper elevation={10} className="homeRightCard">
                                <Box className="homeIllustration">
                                    <img srcSet='/undraw_mobile-app-data_2lfx.svg' alt="Meeting illustration" />
                                </Box>
                                <Stack spacing={1.5} sx={{ mt: 3 }}>
                                    <Typography variant="h5" fontWeight={500}>
                                        Sleek controls. Symmetrical layout.
                                    </Typography>
                                    <Typography color="text.secondary" variant="body2" sx={{ lineHeight: 1.5 }}>
                                        Experience high-contrast dark theme controls, responsive layouts, and collapsible panels to keep your meeting experience clean and professional.
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </>
    )
}


export default withAuth(HomeComponent)